import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ASSET_CATEGORIES, ASSET_CONDITIONS, CONDITION_LABELS, canEditData, formatDate, formatIDR, today } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";

const emptyForm = {
  name: "",
  category: "armada_layanan",
  purchase_date: today(),
  purchase_value: "",
  useful_life_years: "4",
  condition: "baik",
  location: "",
};

export default function Aset() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets");
      setAssets(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const summary = useMemo(() => {
    return {
      purchase: assets.reduce((s, a) => s + a.purchase_value, 0),
      accumulated: assets.reduce((s, a) => s + a.accumulated_depreciation, 0),
      book: assets.reduce((s, a) => s + a.book_value, 0),
    };
  }, [assets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.purchase_value || Number(form.purchase_value) <= 0) {
      toast.error("Lengkapi nama aset dan nilai perolehan");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      purchase_value: Number(form.purchase_value),
      useful_life_years: Number(form.useful_life_years) || 1,
    };
    try {
      if (editingId) {
        await api.put(`/assets/${editingId}`, payload);
        toast.success("Aset berhasil diperbarui");
      } else {
        await api.post("/assets", payload);
        toast.success("Aset berhasil ditambahkan");
      }
      setEditingId(null);
      setForm(emptyForm);
      fetchAssets();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (a) => {
    setEditingId(a.asset_id);
    setForm({
      name: a.name,
      category: a.category,
      purchase_date: a.purchase_date,
      purchase_value: String(a.purchase_value),
      useful_life_years: String(a.useful_life_years),
      condition: a.condition,
      location: a.location || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (a) => {
    if (confirmDelete !== a.asset_id) {
      setConfirmDelete(a.asset_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/assets/${a.asset_id}`);
      toast.success("Aset dihapus");
      setConfirmDelete(null);
      fetchAssets();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="aset-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">Aset Kelolaan</h1>
        <p className="text-sm text-slate-500 mt-1">Pencatatan aset dengan penyusutan tahunan garis lurus</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="aset-total-perolehan">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Total Nilai Perolehan</p>
            <p className="mt-1 text-xl font-bold font-heading text-slate-900">{formatIDR(summary.purchase)}</p>
          </CardContent>
        </Card>
        <Card data-testid="aset-total-penyusutan">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Akumulasi Penyusutan</p>
            <p className="mt-1 text-xl font-bold font-heading text-red-600">{formatIDR(summary.accumulated)}</p>
          </CardContent>
        </Card>
        <Card data-testid="aset-total-buku">
          <CardContent className="p-5">
            <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Total Nilai Buku</p>
            <p className="mt-1 text-xl font-bold font-heading text-emerald-800">{formatIDR(summary.book)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 h-fit">
          <CardHeader>
            <CardTitle className="text-lg">{editingId ? "Ubah Aset" : "Tambah Aset"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="aset-form">
              <div className="space-y-2">
                <Label>Nama Aset</Label>
                <Input
                  data-testid="aset-name-input"
                  placeholder="cth: Mobil Operasional"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="aset-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_CATEGORIES.map((c) => (
                      <SelectItem key={c.key} value={c.key} data-testid={`aset-category-${c.key}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Tgl Perolehan</Label>
                  <Input
                    type="date"
                    data-testid="aset-date-input"
                    value={form.purchase_date}
                    onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Umur (tahun)</Label>
                  <Input
                    type="number"
                    min="1"
                    data-testid="aset-life-input"
                    value={form.useful_life_years}
                    onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nilai Perolehan (Rp)</Label>
                <Input
                  type="number"
                  min="1"
                  data-testid="aset-value-input"
                  placeholder="0"
                  value={form.purchase_value}
                  onChange={(e) => setForm({ ...form, purchase_value: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Kondisi</Label>
                  <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                    <SelectTrigger data-testid="aset-condition-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_CONDITIONS.map((c) => (
                        <SelectItem key={c.key} value={c.key} data-testid={`aset-condition-${c.key}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lokasi</Label>
                  <Input
                    data-testid="aset-location-input"
                    placeholder="Opsional"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  data-testid="aset-submit-button"
                  disabled={saving}
                  className="flex-1 bg-emerald-800 hover:bg-emerald-700 text-white"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Simpan Perubahan" : "Simpan Aset"}
                </Button>
                {editingId && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="aset-cancel-edit-button"
                    onClick={() => { setEditingId(null); setForm(emptyForm); }}
                  >
                    Batal
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Daftar Aset ({assets.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[560px] overflow-auto">
              <Table data-testid="aset-table">
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead>Aset</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead className="text-right">Nilai Perolehan</TableHead>
                    <TableHead className="text-right">Susut/Tahun</TableHead>
                    <TableHead className="text-right">Akum. Susut</TableHead>
                    <TableHead className="text-right">Nilai Buku</TableHead>
                    <TableHead>Kondisi</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin inline-block" />
                      </TableCell>
                    </TableRow>
                  ) : assets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10 text-slate-400" data-testid="aset-empty">
                        Belum ada aset tercatat
                      </TableCell>
                    </TableRow>
                  ) : (
                    assets.map((a) => (
                      <TableRow key={a.asset_id} data-testid={`aset-row-${a.asset_id}`}>
                        <TableCell>
                          <p className="text-sm font-medium text-slate-800">{a.name}</p>
                          <p className="text-xs text-slate-400">{formatDate(a.purchase_date)}{a.location ? ` · ${a.location}` : ""}</p>
                        </TableCell>
                        <TableCell>
                          <span className="inline-block rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800 whitespace-nowrap">
                            {a.category_label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">{formatIDR(a.purchase_value)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap text-slate-500">{formatIDR(a.annual_depreciation)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap text-red-600">{formatIDR(a.accumulated_depreciation)}</TableCell>
                        <TableCell className="text-right font-semibold whitespace-nowrap text-emerald-800">{formatIDR(a.book_value)}</TableCell>
                        <TableCell>
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${
                            a.condition === "baik" ? "bg-emerald-100 text-emerald-800"
                            : a.condition === "rusak_ringan" ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-700"
                          }`}>
                            {CONDITION_LABELS[a.condition] || a.condition}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {canEditData(user?.role) && (
                          <div className="flex justify-end gap-1">
                            <button
                              data-testid={`aset-edit-${a.asset_id}`}
                              onClick={() => handleEdit(a)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`aset-delete-${a.asset_id}`}
                              onClick={() => handleDelete(a)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${
                                confirmDelete === a.asset_id
                                  ? "bg-red-600 text-white"
                                  : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                              }`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
